import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { getPublicSetting } from '../../utils/publicSettings';
import api from '../../api/axios';

export interface MeasurementData {
  cap_size?: string;
  agbada_length?: string;
  agbada_width?: string;
  top_length?: string;
  top_back?: string;
  top_sleeve?: string;
  top_chest?: string;
  top_body?: string;
  top_neck?: string;
  top_wrist?: string;
  top_arm?: string;
  pant_length?: string;
  pant_waist?: string;
  pant_hip?: string;
  pant_ankle?: string;
  pant_thigh?: string;
  comment?: string;
}

interface MeasurementModalProps {
  onClose: () => void;
  onSubmit: (measurements: MeasurementData) => void;
  isSubmitting?: boolean;
  categories?: string[];
}

const getYoutubeEmbedUrl = (url: string): string | null => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (match) return `https://www.youtube.com/embed/${match[1]}`;
  return null;
};

const MeasurementModal: React.FC<MeasurementModalProps> = ({ onClose, onSubmit, isSubmitting, categories }) => {
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [form, setForm] = useState<MeasurementData>({});
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [savedMeasurements, setSavedMeasurements] = useState<MeasurementData | null>(null);
  const [showSavedConfirm, setShowSavedConfirm] = useState(false);

  useEffect(() => {
    const fetchVideoUrl = async () => {
      const url = await getPublicSetting('measurement_video_url', '');
      if (url) setVideoUrl(url);
    };
    fetchVideoUrl();

    const fetchSavedMeasurements = async () => {
      try {
        const res = await api.get('/api/users/measurements');
        const saved = res.data?.data;
        if (saved && typeof saved === 'object' && Object.keys(saved).length > 0) {
          setSavedMeasurements(saved);
          setForm(saved);
          setShowSavedConfirm(true);
        }
      } catch {
        // no saved measurements yet — start with a blank form, nothing to do
      }
    };
    fetchSavedMeasurements();
  }, []);

  const updateField = (field: keyof MeasurementData, value: string) => {
    // Numbers only for actual measurement fields — digits and at most one
    // decimal point, typed characters that don't match are just dropped.
    // The "comment" field stays free text since it's notes for the tailor.
    const sanitized = field === 'comment'
      ? value
      : value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setForm((prev) => ({ ...prev, [field]: sanitized }));
    if (sanitized.trim()) {
      setErrors((prev) => {
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
    }
  };

  const embedUrl = videoUrl ? getYoutubeEmbedUrl(videoUrl) : null;

  const allSections: { key: string; title: string; fields: { key: keyof MeasurementData; label: string }[] }[] = [
    {
      key: 'cap',
      title: 'Cap',
      fields: [{ key: 'cap_size', label: 'Cap Size' }],
    },
    {
      key: 'agbada',
      title: 'Agbada',
      fields: [
        { key: 'agbada_length', label: 'Length' },
        { key: 'agbada_width', label: 'Width' },
      ],
    },
    {
      key: 'top',
      title: 'Top',
      fields: [
        { key: 'top_length', label: 'Length' },
        { key: 'top_back', label: 'Back' },
        { key: 'top_sleeve', label: 'Sleeve' },
        { key: 'top_chest', label: 'Chest' },
        { key: 'top_body', label: 'Body' },
        { key: 'top_neck', label: 'Neck' },
        { key: 'top_wrist', label: 'Wrist' },
        { key: 'top_arm', label: 'Arm' },
      ],
    },
    {
      key: 'pant',
      title: 'Pant',
      fields: [
        { key: 'pant_length', label: 'Length' },
        { key: 'pant_waist', label: 'Waist' },
        { key: 'pant_hip', label: 'Hip' },
        { key: 'pant_ankle', label: 'Ankle' },
        { key: 'pant_thigh', label: 'Thigh' },
      ],
    },
  ];

  // Only show the sections this specific product actually needs. If no
  // categories were assigned by admin (older products, or a mistake),
  // fall back to showing everything rather than an empty, useless form.
  const activeCategories = categories && categories.length > 0 ? categories : allSections.map((s) => s.key);
  const sections = allSections.filter((s) => activeCategories.includes(s.key));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const missing = new Set<string>();
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (!form[field.key] || !form[field.key]!.trim()) {
          missing.add(field.key);
        }
      });
    });

    if (missing.size > 0) {
      setErrors(missing);
      return;
    }

    setErrors(new Set());
    onSubmit(form);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-light flex items-center justify-between px-6 py-4 z-10">
            <h2 className="font-serif text-xl text-primary">Your Measurements</h2>
            <button onClick={onClose} className="text-nude hover:text-primary transition-colors">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-8">
            <p className="text-xs text-secondary leading-relaxed">
              This piece is made to measure. Please provide the measurements below so it can be tailored to fit you. All fields are required.
            </p>

            {showSavedConfirm && savedMeasurements && (
              <div className="bg-amber-50 border-2 border-amber-200 p-4">
                <p className="text-sm text-primary font-medium mb-1">
                  We have your measurements on file
                </p>
                <p className="text-xs text-secondary leading-relaxed mb-3">
                  We've pre-filled the form below with the measurements you provided last time. Are they still accurate?
                </p>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowSavedConfirm(false)}
                    className="btn-primary text-xs py-2"
                  >
                    Yes, Still Accurate
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSavedConfirm(false)}
                    className="btn-secondary text-xs py-2"
                  >
                    No, Let Me Update
                  </button>
                </div>
              </div>
            )}

            {embedUrl && (
              <div>
                <p className="text-xs tracking-widest uppercase text-nude mb-3">
                  How to Take Your Measurements
                </p>
                <div className="aspect-video bg-light">
                  <iframe
                    src={embedUrl}
                    title="How to take your measurements"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {sections.map((section) => (
                <div key={section.title}>
                  <p className="text-xs tracking-widest uppercase text-primary font-semibold mb-3 pb-2 border-b-2 border-secondary/20">
                    {section.title}
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {section.fields.map((field) => (
                      <div key={field.key}>
                        <label className="text-xs text-secondary font-medium block mb-1.5">
                          {field.label} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={form[field.key] || ''}
                            onChange={(e) => updateField(field.key, e.target.value)}
                            className={`w-full bg-white border-2 rounded-sm px-3 py-2.5 text-sm text-primary font-medium outline-none transition-colors ${
                              errors.has(field.key)
                                ? 'border-red-500'
                                : 'border-secondary/30 focus:border-primary'
                            }`}
                            placeholder="0"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-secondary pointer-events-none">
                            in
                          </span>
                        </div>
                        {errors.has(field.key) && (
                          <p className="text-[10px] text-red-500 mt-1 font-medium">Required</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div>
                <label className="text-xs tracking-widest uppercase text-primary font-semibold block mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={form.comment || ''}
                  onChange={(e) => updateField('comment', e.target.value.slice(0, 3000))}
                  rows={4}
                  maxLength={3000}
                  placeholder="Any special instructions for your tailor..."
                  className="w-full border-2 border-secondary/30 focus:border-primary p-3 text-sm text-primary outline-none transition-colors resize-none rounded-sm"
                />
              </div>

              {errors.size > 0 && (
                <p className="text-xs text-red-500 text-center">
                  Please fill in all required measurements before continuing.
                </p>
              )}

              <div className="flex space-x-4 pb-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {isSubmitting ? 'Adding...' : 'Submit & Add to Bag'}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MeasurementModal;